import React, { useState, useEffect } from 'react'
import { Box, Divider, Typography } from '@mui/material'
import AddIconButton from '../ui/button/AddIconButton'
import { useAppSelector } from '../../store'
import { selectEater } from '../../store/profileSlice'
import { mapSubscriptionPlanIdToTier } from '../../helpers/tier-helper'
import { fetchWalletBalance } from '../../services/rewardsService'

interface PointsSummaryCardProps {
  onRedeemPoints: () => void
  isSubscribed?: boolean
  points?: number // Fallback if balance fetch fails
}

const PointsSummaryCard: React.FC<PointsSummaryCardProps> = ({
  onRedeemPoints,
  isSubscribed = true, // Default to true to maintain backward compatibility
  points: fallbackPoints = 0,
}) => {
  const [pointsBalance, setPointsBalance] = useState<number | null>(null)
  const eater = useAppSelector(selectEater)
  
  // Fetch balance to get accurate points
  useEffect(() => {
    const fetchBalance = async (): Promise<void> => {
      try {
        const balance = await fetchWalletBalance()
        setPointsBalance(balance.walletAvailableCents ?? null)
      } catch (error) {
        // Fallback to prop points if balance fetch fails
        setPointsBalance(null)
      }
    }
    void fetchBalance()
  }, [])
  
  // Use balance points if available, otherwise fallback to prop
  const points = pointsBalance !== null ? pointsBalance : fallbackPoints
  const mappedPlanName = mapSubscriptionPlanIdToTier(eater?.subscriptionPlanId)
  const isFreeTier = !mappedPlanName || mappedPlanName === null

  const buttonText = isSubscribed
    ? 'View Rewards'
    : isFreeTier
      ? 'View Rewards'
      : 'View Rewards'
  
  const subtext = isSubscribed
    ? 'Earn points to unlock credits and redeem rewards.'
    : isFreeTier
      ? 'Stay active to unlock credits over time. Credits are subject to availability.'
      : 'Earn points to unlock credits and redeem rewards.'

  return (
    <Box
      sx={{
        backgroundColor: 'secondary.50',
        py: { xs: 2, md: 4 },
        px: { xs: 1.5, md: 3 },
        borderRadius: '12px',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Box display="flex" alignItems="center" mb={1}>
            <Box component="img" src="/images/leaderboard/coin-yellow.png" />
            <Typography variant="body2" pl={0.5}>
              {points.toLocaleString()} Credits
            </Typography>
          </Box>
        </Box>
        <AddIconButton
          variant="contained"
          sx={{ alignSelf: { xs: 'self-start', md: 'center' } }}
          onClick={onRedeemPoints}
        >
          {buttonText}
        </AddIconButton>
      </Box>
      <Divider sx={{ color: 'grey.300', mt: 3, mb: 2 }} />
      <Typography variant="subtitle2" color="grey.600">
        1. Points track your progress toward monthly goals.
      </Typography>
      <Typography variant="subtitle2" color="grey.600">
        2. {subtext}
      </Typography>
    </Box>
  )
}

export default PointsSummaryCard
